import React from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Stack,
  Skeleton,
} from '@mui/material'
import {
  SmokingRooms as SmokeIcon,
  Add as AddIcon,
  Search as FindIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { useGetStatsQuery } from '@store/api/userApi'
import { useAppSelector } from '@store/hooks'
import { formatAverageRatingDisplay } from '@utils/statDisplay'
import { trackFeature } from '@utils/trackFeature'
import { useActiveSmokingSession } from '@hooks/useActiveSmokingSession'
import ActiveSessionCard from '@components/sessions/ActiveSessionCard'

type InsightKind = 'cigars' | 'sessions' | 'last_smoked' | 'rating'

function formatLastSmokedLabel(iso: string | null | undefined): string {
  if (!iso) return 'No sessions yet'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  try {
    return formatDistanceToNow(date, { addSuffix: true })
  } catch {
    return '—'
  }
}

function isEmptyAccount(stats: { totalCigars: number; totalSmokingSessions: number }): boolean {
  return stats.totalCigars === 0 && stats.totalSmokingSessions === 0
}

const Dashboard: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const navigate = useNavigate()
  const { activeSession } = useActiveSmokingSession()
  const {
    data: stats,
    isLoading,
    isError,
  } = useGetStatsQuery(undefined, {
    skip: !isAuthenticated,
  })

  const showInsightStrip =
    !isError && (isLoading || (stats != null && !isEmptyAccount(stats)))

  const handleInsightTap = (kind: InsightKind, path: string) => {
    trackFeature('insight_tap', { tile: kind })
    if (kind === 'rating') {
      trackFeature('analytics_open', { source: 'home_insight' })
    }
    navigate(path)
  }

  const handleSmokeNow = () => {
    trackFeature('cta_smoke_start', { source: 'home' })
    navigate('/app/journal')
  }

  const handleAddCigar = () => {
    trackFeature('cta_cigar_add', { status: 'opened', source: 'home' })
    navigate('/app/collection', { state: { openAddCigar: true } })
  }

  const handleFind = () => {
    navigate('/app/database')
  }

  const ratingHasValue =
    stats?.averageRating != null && Number.isFinite(stats.averageRating)

  return (
    <>
      <Typography variant="h4" component="h1" gutterBottom>
        Home
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Resume a smoke, glance at your stats, or jump to your next action.
      </Typography>

      <Stack spacing={3} sx={{ mt: 2 }} data-testid="home-stack">
        {activeSession && (
          <Box data-testid="home-active-session">
            <ActiveSessionCard session={activeSession} />
          </Box>
        )}

        {showInsightStrip && (
          <Box data-testid="home-insight-strip">
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <InsightTile
                  title="Cigars"
                  loading={isLoading}
                  value={stats?.totalCigars}
                  onClick={() => handleInsightTap('cigars', '/app/collection')}
                  testId="home-insight-cigars"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <InsightTile
                  title="Sessions"
                  loading={isLoading}
                  value={stats?.totalSmokingSessions}
                  onClick={() => handleInsightTap('sessions', '/app/journal')}
                  testId="home-insight-sessions"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <InsightTile
                  title="Last smoked"
                  loading={isLoading}
                  valueLabel={
                    isLoading ? undefined : formatLastSmokedLabel(stats?.lastSession)
                  }
                  onClick={() => handleInsightTap('last_smoked', '/app/journal')}
                  testId="home-insight-last-smoked"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                {isLoading ? (
                  <InsightTile
                    title="Avg rating"
                    loading
                    onClick={() => handleInsightTap('rating', '/app/analytics')}
                    testId="home-insight-rating"
                  />
                ) : ratingHasValue ? (
                  <InsightTile
                    title="Avg rating"
                    valueLabel={formatAverageRatingDisplay(stats?.averageRating)}
                    onClick={() => handleInsightTap('rating', '/app/analytics')}
                    testId="home-insight-rating"
                  />
                ) : (
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea
                      onClick={() => handleInsightTap('rating', '/app/analytics')}
                      data-testid="home-insight-rating"
                      sx={{ height: '100%', minHeight: 88 }}
                    >
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Ratings
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="primary">
                          Rate your smokes
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          data-testid="home-shortcuts"
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<SmokeIcon />}
            onClick={handleSmokeNow}
            sx={{ minHeight: 48, flex: { sm: 1 } }}
          >
            Smoke now
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<AddIcon />}
            onClick={handleAddCigar}
            sx={{ minHeight: 48, flex: { sm: 1 } }}
          >
            Add cigar
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<FindIcon />}
            onClick={handleFind}
            sx={{ minHeight: 48, flex: { sm: 1 } }}
          >
            Find
          </Button>
        </Stack>
      </Stack>
    </>
  )
}

type InsightTileProps = {
  title: string
  loading?: boolean
  value?: number
  valueLabel?: string
  onClick: () => void
  testId: string
}

function InsightTile({ title, loading, value, valueLabel, onClick, testId }: InsightTileProps) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea
        onClick={onClick}
        disabled={Boolean(loading)}
        data-testid={testId}
        sx={{ height: '100%', minHeight: 88 }}
      >
        <CardContent>
          <Typography variant="caption" color="text.secondary" display="block">
            {title}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width="60%" height={36} />
          ) : (
            <Typography variant="h5" component="p" color="primary">
              {valueLabel ?? value ?? 0}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export default Dashboard
